import PropTypes from "prop-types";
import React, { useEffect, useState, useContext } from "react";
import "../../assets/css/login.css";
import {
	Row,
	Col,
	CardBody,
	Card,
	Alert,
	Container,
	Form,
	Input,
	FormFeedback,
	Label,
	InputGroup,
	InputGroupText,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { Eye, EyeOff } from "lucide-react";
import { CheckLogin, consumeRedirectLogin, sessionValidationCheck } from "../../api/loginService";
import withRouter from "../../components/Common/withRouter";
import * as Yup from "yup";
import { useFormik } from "formik";
import AuthContext from "../../Context/AuthContext";
import { createSelector } from "reselect";
import { encrypt, decrypt } from "../../helpers/EncryptDecryptPassword";
import CommonToastContainer from "../../components/Common/CommonToastContainer";
import FullScreenLoader from "../../components/Common/Loader";
import { clearForceLogout } from "../../Context/authLogout";
import { useNavigate } from "react-router-dom";

const Login = (props) => {
	document.title = "Login";

	const [showPassword, setShowPassword] = useState(false);
	const [rememberMe, setRememberMe] = useState(false);
	const [showLoader, setShowLoader] = useState(false);
	const [loginError, setLoginError] = useState("");
	const [toasts, setToasts] = useState([]);
	const [progress, setProgress] = useState(0);
	const { login } = useContext(AuthContext);
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const showToast = (toastData) => {
		setToasts((prev) => [...prev, { id: Date.now(), ...toastData }]);
	};

	const removeToast = (id) => {
		setToasts((prev) => prev.filter((t) => t.id !== id));
	};

	useEffect(() => {
		const savedUser = localStorage.getItem("rememberedUser");
		const savedPass = localStorage.getItem("rememberedPassword");

		if (savedUser) {
			validation.setFieldValue("usernameOrEmail", decrypt(savedUser));
			setRememberMe(true);
		}
		if (savedPass) {
			validation.setFieldValue("password", decrypt(savedPass));
		}
	}, []);

	const validation = useFormik({
		initialValues: {
			usernameOrEmail: "",
			password: "",
		},
		validationSchema: Yup.object({
			usernameOrEmail: Yup.string().required("Please enter your Username or Email"),
			password: Yup.string().required("Please enter your Password"),
		}),
		onSubmit: async (values, { setSubmitting }) => {
		
			try {
				const response = await CheckLogin({
					Username: values.usernameOrEmail,
					Password: values.password,
				});
				console.log("Rsponse", response);
				if (response?.Message?.[0]?.RETURNCODE === "1") {
					setShowLoader(true);
					const user = response?.UserDetail?.[0];
					localStorage.setItem("authToken", user?.JWTTOKEN);
					localStorage.setItem("authUser", JSON.stringify(user));
					login({
						LEDGER_ID: user?.LEDGER_ID,
						TOKEN: user?.JWTTOKEN,
						COMPANYLOGO: user?.COMPANYLOGO,
						COMPANYNAME: user?.COMPANYNAME,
						USERNAME: user?.USERNAME,
						PROFILEIMAGE: user?.PROFILEIMAGE,
					});

					if (rememberMe) {
						localStorage.setItem("rememberedUser", encrypt(values.usernameOrEmail));
						localStorage.setItem("rememberedPassword", encrypt(values.password));
					} else {
						localStorage.removeItem("rememberedUser");
						localStorage.removeItem("rememberedPassword");
					}

					if (user.SELLERMOBILENO) localStorage.setItem("salesPersonWA", user.SELLERMOBILENO);
					else if (user.SELLERPHONENO) localStorage.setItem("salesPersonWA", user.SELLERPHONENO);
						
					showToast({
						title: "Success!",
						message: <>Welcome back, <b>{user?.USERNAME}</b></>,
						type: "success",
					});

					setTimeout(() => props.router.navigate("/vault-access"), 2000);
				} else {
					setShowLoader(false); 
					setLoginError(response?.Message?.[0]?.RETURNMESSAGE || "Login failed");
				}
			} catch {
				setShowLoader(false); 
				setLoginError("Something went wrong. Please try again.");
			} finally {
				setProgress(100);

				setTimeout(() => {
					setShowLoader(false);
					setProgress(0);
				}, 300);
			}
		},
	});

	const loginpage = createSelector(
		(state) => state.login,
		(state) => ({ error: state.error })
	);

	const { error } = useSelector(loginpage);

	useEffect(() => {
		const token = new URLSearchParams(window.location.search).get("token");
		const existingToken = localStorage.getItem("authToken");
		const existingUser = JSON.parse(localStorage.getItem("authUser") || "null");

		if (!token) return;

		const handleSSO = async () => {
			setShowLoader(true);
			try {
				const res = await consumeRedirectLogin(token);
				const incomingUsername = res?.user?.USERNAME;

				if (existingToken) {
					const validationResult = await sessionValidationCheck();

					if (validationResult?.Message?.[0]?.RETURNCODE === "1") {
						if (existingUser?.USERNAME === incomingUsername) {
							navigate("/vault-access");
							return;
						}
						localStorage.removeItem("authToken");
						localStorage.removeItem("authUser");
						localStorage.removeItem("LOGIN_RESPONSE");
						localStorage.removeItem("LOGIN_TIME");
						clearForceLogout();
					} else {
						localStorage.removeItem("authToken");
						localStorage.removeItem("authUser");
						localStorage.removeItem("LOGIN_RESPONSE");
						localStorage.removeItem("LOGIN_TIME");
						clearForceLogout();
					}
				}

				localStorage.setItem("authToken", res.token);
				localStorage.setItem("authUser", JSON.stringify(res.user));
				login(res.user);

				window.history.replaceState({}, document.title, "/");
				navigate("/");

			} catch (error) {
				showToast({
					title: "SSO Login Failed",
					message:
						error?.response?.data?.message ||
						"SSO login failed. Please login manually.",
					type: "error",
				});

				window.history.replaceState({}, document.title, "/login");
			} finally {
				setShowLoader(false);
			}
		};
		handleSSO();
	}, []);

	return (
		<>
			{showLoader && <FullScreenLoader progress={progress} />}
			<div
				className="account-pages login-background"
				style={{
					backgroundImage: `linear-gradient(rgba(0,0,0,.20), rgba(0,0,0,.20)), url(/assets/images/backgroundImages/login_bg.jpg)`,
					backgroundSize: "cover",
				}}
			>
				<Container>
					<Row className="justify-content-center">
						<Col lg={6} md={8} xl={4}>
							<Card className="login-card p-3 mb-0" style={{
								borderRadius: "28px",
								overflow: "hidden",
								backgroundColor: "#ffffff"
							}}>
								<div className="login-banner">
									<img
										src="/assets/images/Logo/Midamas_Logo_2_15.png"
										alt="Midamas"
									/>
								</div>

								<CardBody className="login-card-body">
									<div className="text-center mt-4 mb-4">
										<h4 className="fw-semibold">Welcome Back!</h4>
										<p className="text-muted mb-0">
											Sign in to continue to Midamas
										</p>
									</div>

									<Form onSubmit={validation.handleSubmit}>
										{error && <Alert color="danger">{error}</Alert>}
										
										<div className="mb-3">
											<Label htmlFor="UsernameorEmail">Username or Email</Label>
											<Input
												id="UsernameorEmail"
												name="usernameOrEmail"
												placeholder="Enter your username or email"
												value={validation.values.usernameOrEmail}
												onChange={validation.handleChange}
												onBlur={validation.handleBlur}
												invalid={validation.touched.usernameOrEmail && !!validation.errors.usernameOrEmail}
											/>
											<FormFeedback>{validation.errors.usernameOrEmail}</FormFeedback>
										</div>

										<div className="mb-3">
											<Label htmlFor="password">Password</Label>
											<InputGroup>
												<Input
													id="password"
													type={showPassword ? "text" : "password"}
													name="password"
													placeholder="Enter your password"
													value={validation.values.password}
													onChange={validation.handleChange}
													onBlur={validation.handleBlur}
													invalid={validation.touched.password && !!validation.errors.password}
												/>
												<InputGroupText onClick={() => setShowPassword(!showPassword)}>
													{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
												</InputGroupText>
												<FormFeedback>{validation.errors.password}</FormFeedback>
											</InputGroup>
										</div>

										<div className="remember-row">
											<label className="custom-check remember-check">
												<input
													type="checkbox"
													checked={rememberMe}
													onChange={(e) => setRememberMe(e.target.checked)}
												/>
												<span className="checkmark"></span>
												<span className="check-label">Remember me</span>
											</label>

											<a
												href="/forget-password"
												className="login-hover-link forgot-link"
											>
												Forgot password?
											</a>
										</div>

										<button className="btn btn-primary w-100" disabled={showLoader}>
											{showLoader ? "Loading..." : "Log In"}
										</button>

										{loginError && (
											<div className="text-danger text-center mt-2 fw-semibold">
												{loginError}
											</div>
										)}

										<div className="text-center my-2">
											<a href="/register" className="text-primary fw-semibold login-hover-link">
												Register
											</a>
										</div>
									</Form>
								</CardBody>
							</Card>
						</Col>
					</Row>
				</Container>
			</div>

			<CommonToastContainer toasts={toasts} removeToast={removeToast} />
		</>
	);
};

export default withRouter(Login);

Login.propTypes = {
	history: PropTypes.object,
};
